'use client';

import clsx from 'clsx';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { useCTAHeight } from './hooks/useCTAHeight';
import { Snackbar } from './Snackbar';
import { SnackbarContext } from './SnackbarProvider';
import type { SnackbarProps } from './types';

function useMainContainerWidth() {
  const [maxWidth, setMaxWidth] = useState(600); // fallback

  useEffect(() => {
    const detectWidth = () => {
      const mainElement = document.querySelector('main');
      if (mainElement) {
        const rect = mainElement.getBoundingClientRect();
        const computedStyle = getComputedStyle(mainElement);
        const maxWidthValue = computedStyle.maxWidth;

        let containerWidth = 600;

        if (maxWidthValue && maxWidthValue !== 'none') {
          const parsedMaxWidth = parseInt(maxWidthValue, 10);
          if (!Number.isNaN(parsedMaxWidth)) {
            containerWidth = parsedMaxWidth;
          }
        } else if (rect.width > 0) {
          containerWidth = Math.max(rect.width, 320);
        }

        setMaxWidth(containerWidth);
      }
    };

    detectWidth();

    const handleResize = () => {
      setTimeout(detectWidth, 50);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return maxWidth;
}

const SNACKBAR_CONTAINER_HEIGHT = 100;
const SNACKBAR_BOTTOM_SPACING = 12;
const ANIMATION_DELAY = 50;
const EXIT_ANIMATION_DURATION = 200;

const snackbarVariants = tv({
  base: 'absolute',
  variants: {
    state: {
      hidden: 'opacity-0 scale-95',
      visible: 'opacity-100 scale-100',
      exiting: 'opacity-0 scale-95',
    },
    transition: {
      none: 'transition-none',
      enter: 'transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
      exit: 'transition-all duration-200 ease-[cubic-bezier(0.4,0,1,1)]',
    },
  },
});

interface AnimatedSnackbarProps {
  snackbar: SnackbarProps;
  index: number;
  onClose: (id: string) => void;
  isExiting: boolean;
}

function AnimatedSnackbar({ snackbar, index, onClose, isExiting }: AnimatedSnackbarProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [snackbarHeight, setSnackbarHeight] = useState(0);
  const snackbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!snackbarRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        if (height > 0 && height !== snackbarHeight) {
          setSnackbarHeight(height);

          if (!isVisible && snackbarHeight === 0) {
            setTimeout(() => setIsVisible(true), ANIMATION_DELAY);
          }
        }
      }
    });

    resizeObserver.observe(snackbarRef.current);

    const fallbackMeasure = () => {
      if (snackbarRef.current) {
        const height = snackbarRef.current.offsetHeight;
        if (height > 0 && snackbarHeight === 0) {
          setSnackbarHeight(height);
          setTimeout(() => setIsVisible(true), ANIMATION_DELAY);
        }
      }
    };

    setTimeout(fallbackMeasure, 0);

    return () => {
      resizeObserver.disconnect();
    };
  }, [snackbarHeight, isVisible]);

  const getState = () => {
    if (isExiting) return 'exiting';
    if (isVisible) return 'visible';
    return 'hidden';
  };

  const getTransition = () => {
    if (isExiting) return 'exit';
    if (isVisible) return 'enter';
    return 'none';
  };

  const getBottomPosition = () => {
    if (snackbarHeight === 0) return -1000;
    if (isExiting) return -(snackbarHeight + 50);
    return isVisible ? 0 : -snackbarHeight;
  };

  return (
    <div
      className={snackbarVariants({
        state: getState(),
        transition: getTransition(),
      })}
      style={{
        bottom: getBottomPosition(),
        zIndex: 1000 + index,
        left: '16px',
        right: '16px',
      }}
      ref={snackbarRef}
    >
      <Snackbar {...snackbar} onClose={() => onClose(snackbar.id)} />
    </div>
  );
}

export function SnackbarContainer() {
  const context = useContext(SnackbarContext);
  const [mounted, setMounted] = useState(false);
  const [exitingSnackbars, setExitingSnackbars] = useState<Set<string>>(new Set());

  const ctaHeight = useCTAHeight({ enabled: mounted });
  const dynamicMaxWidth = useMainContainerWidth();

  const handleAnimatedClose = useCallback(
    (id: string) => {
      if (!context) return;
      setExitingSnackbars((prev) => new Set(Array.from(prev).concat(id)));
      setTimeout(() => {
        context.close(id);
        setExitingSnackbars((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, EXIT_ANIMATION_DURATION);
    },
    [context],
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (context?.setAnimatedClose) {
      context.setAnimatedClose(handleAnimatedClose);
    }
  }, [context, handleAnimatedClose]);

  if (!context || !mounted) {
    return null;
  }

  const { snackbars } = context;

  if (snackbars.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx('fixed')}
      style={{
        bottom: `calc(env(safe-area-inset-bottom, 0px) + ${SNACKBAR_BOTTOM_SPACING + ctaHeight}px)`,
        left: 0,
        right: 0,
        zIndex: 99999,
        height: `${SNACKBAR_CONTAINER_HEIGHT}px`,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          maxWidth: `${dynamicMaxWidth}px`,
          margin: '0 auto',
          height: '100%',
          position: 'relative',
        }}
      >
        {snackbars.map((snackbar, index) => (
          <AnimatedSnackbar
            key={snackbar.id}
            snackbar={snackbar}
            index={index}
            onClose={handleAnimatedClose}
            isExiting={exitingSnackbars.has(snackbar.id)}
          />
        ))}
      </div>
    </div>
  );
}
